import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Camera, Loader2 } from "lucide-react";

import { useFeedbackContext } from "@/context/FeedbackContext";
import { useFeedbackCapture } from "@/hooks/useFeedbackCapture";
import { useSubmitFeedback, type FeedbackType } from "@/hooks/useFeedback";
import { ScreenshotPreview } from "@/components/feedback/ScreenshotPreview";
import { ImageAttachmentsPreview } from "@/components/feedback/ImageAttachmentsPreview";
import { getConsoleLogs } from "@/lib/consoleCapture";

const MAX_ATTACHMENT_COUNT = 8;
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024; // 10 MB per file
const MAX_TOTAL_ATTACHMENT_BYTES = 40 * 1024 * 1024; // 40 MB total per report
const ATTACHMENT_ACCEPT_ATTR =
 "image/*,video/*,audio/*,.pdf,.txt,.md,.csv,.json,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip";

const ALLOWED_ATTACHMENT_MIME_PREFIXES = ["image/", "video/", "audio/", "text/"];
const ALLOWED_ATTACHMENT_MIME_TYPES = new Set([
 "application/pdf",
 "application/json",
 "application/zip",
 "application/msword",
 "application/vnd.ms-excel",
 "application/vnd.ms-powerpoint",
 "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
 "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
 "application/vnd.openxmlformats-officedocument.presentationml.presentation",
]);
const ALLOWED_ATTACHMENT_EXTENSIONS = new Set([
 "pdf", "txt", "md", "csv", "json", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "zip",
 "jpg", "jpeg", "png", "gif", "webp", "mp4", "mov", "webm", "mp3", "wav",
]);

function isAllowedAttachmentFile(file: File): boolean {
 if (ALLOWED_ATTACHMENT_MIME_PREFIXES.some((prefix) => file.type.startsWith(prefix))) return true;
 if (ALLOWED_ATTACHMENT_MIME_TYPES.has(file.type)) return true;
 const extension = file.name.split(".").pop()?.toLowerCase();
 return !!extension && ALLOWED_ATTACHMENT_EXTENSIONS.has(extension);
}

export function FeedbackForm() {
 const { closeFeedback, openOptions } = useFeedbackContext();
 const submit = useSubmitFeedback();

 const {
 isCapturing,
 screenshot,
 consoleLogs,
 setScreenshot,
 setConsoleLogs,
 captureAll,
 clear,
 } = useFeedbackCapture();

 const [type, setType] = useState<FeedbackType>("bug");
 const [subject, setSubject] = useState("");
 const [description, setDescription] = useState("");
 const [photoFiles, setPhotoFiles] = useState<File[]>([]);

 const errorSummary = useMemo(() => {
 const msg = openOptions?.error?.message;
 return msg ? msg.slice(0, 180) : null;
 }, [openOptions?.error?.message]);

 // Auto-capture when opened from ErrorBoundary
 useEffect(() => {
 if (!openOptions?.autoCaptureScreenshot) return;
 if (screenshot) return;

 captureAll({
 // IMPORTANT: Don't hide the sheet on auto-capture. On slow/stalled html2canvas
 // renders, hiding the sheet makes it look like the "Report this issue" button
 // doesn't open anything (overlay shows, panel stays invisible).
 hideSelectors: ["[data-feedback-widget]"],
 }).catch((err) => {
 console.warn("[FeedbackForm] Auto-capture failed", err);
 });
 }, [openOptions?.autoCaptureScreenshot, screenshot, captureAll]);

 // If ErrorBoundary opened us, prefill type + subject lightly
 useEffect(() => {
 if (openOptions?.source !== "error_boundary") return;
 setType("bug");
 if (errorSummary) {
 setSubject((prev) => prev || `Error: ${errorSummary}`);
 }
 }, [openOptions?.source, errorSummary]);

 const handleCaptureClick = async () => {
 try {
 await captureAll({ hideSelectors: ["[data-feedback-widget]", "[data-feedback-sheet]"] });
 toast.success("Captured screenshot");
 } catch (err) {
 console.warn("[FeedbackForm] Capture failed", err);
 toast.error("Failed to capture screenshot");
 }
 };

 const handleSubmit = async () => {
 const trimmed = description.trim();
 if (!trimmed) {
 toast.error("Please describe the issue or feedback");
 return;
 }

 try {
 // Ensure we capture console logs even if user didn’t capture screenshot
 const logs = consoleLogs.length ? consoleLogs : getConsoleLogs();
 if (consoleLogs.length === 0) setConsoleLogs(logs);

 await submit.mutateAsync({
 type,
 subject: subject.trim() || undefined,
 description: trimmed,
 error: openOptions?.error,
 consoleLogs: logs,
 screenshot,
 attachments: photoFiles,
 });

 toast.success("Feedback sent. Thank you!");
 clear();
 closeFeedback();
 setSubject("");
 setDescription("");
 setType("bug");
 setPhotoFiles([]);
 } catch (err: any) {
 console.error("[FeedbackForm] Submit failed", err);
 toast.error(err?.message || "Failed to submit feedback");
 }
 };

 const isSubmitting = submit.isPending;

 return (
 <div className="space-y-5">
 {openOptions?.error && (
 <div className="rounded-lg border bg-muted/30 p-3">
 <div className="text-sm font-medium">Error detected</div>
 <div className="text-xs text-muted-foreground mt-1">{openOptions.error.message}</div>
 </div>
 )}

 <div className="space-y-2">
 <Label>Type</Label>
 <Select value={type} onValueChange={(v) => setType(v as FeedbackType)}>
 <SelectTrigger>
 <SelectValue placeholder="Select type" />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="bug">Bug</SelectItem>
 <SelectItem value="feature">Feature request</SelectItem>
 <SelectItem value="question">Question</SelectItem>
 <SelectItem value="other">Other</SelectItem>
 </SelectContent>
 </Select>
 </div>

 <div className="space-y-2">
 <Label htmlFor="feedback-subject">Subject</Label>
 <Input
 id="feedback-subject"
 value={subject}
 onChange={(e) => setSubject(e.target.value)}
 placeholder="Short summary (optional)"
 disabled={isSubmitting}
 />
 </div>

 <div className="space-y-2">
 <Label htmlFor="feedback-description">Description</Label>
 <Textarea
 id="feedback-description"
 value={description}
 onChange={(e) => setDescription(e.target.value)}
 placeholder="What happened? What did you expect? Steps to reproduce?"
 className="min-h-[140px]"
 disabled={isSubmitting}
 />
 </div>

 <div className="space-y-2">
 <div className="flex items-center justify-between">
 <Label>Attachments</Label>
 <div className="flex items-center gap-2">
 <input
 id="feedback-attachments"
 type="file"
 accept={ATTACHMENT_ACCEPT_ATTR}
 multiple
 className="hidden"
 onChange={(e) => {
 const picked = Array.from(e.target.files ?? []).filter((f) => f && f.size > 0);
 if (!picked.length) return;

 setPhotoFiles((prev) => {
 const typeAllowed = picked.filter(isAllowedAttachmentFile);
 if (typeAllowed.length < picked.length) {
 toast.error("Some files were skipped — unsupported file type.");
 }

 const sizeAllowed = typeAllowed.filter((f) => f.size <= MAX_ATTACHMENT_BYTES);
 if (sizeAllowed.length < typeAllowed.length) {
 toast.error("Some files were skipped — max 10MB per file.");
 }

 let totalBytes = prev.reduce((sum, f) => sum + f.size, 0);
 const merged = [...prev];
 for (const file of sizeAllowed) {
 if (merged.length >= MAX_ATTACHMENT_COUNT) {
 toast.error("You can attach up to 8 files per report.");
 break;
 }
 if (totalBytes + file.size > MAX_TOTAL_ATTACHMENT_BYTES) {
 toast.error("Total attachment size cannot exceed 40MB.");
 break;
 }
 merged.push(file);
 totalBytes += file.size;
 }
 return merged;
 });

 // allow re-selecting the same file
 e.currentTarget.value = "";
 }}
 disabled={isSubmitting}
 />

 <Button
 type="button"
 variant="outline"
 size="sm"
 onClick={() => document.getElementById("feedback-attachments")?.click()}
 disabled={isSubmitting}
 >
 Add files
 </Button>

 <Button
 type="button"
 variant="outline"
 size="sm"
 onClick={handleCaptureClick}
 disabled={isSubmitting || isCapturing}
 >
 {isCapturing ? (
 <Loader2 className="h-4 w-4 mr-2 animate-spin" />
 ) : (
 <Camera className="h-4 w-4 mr-2" />
 )}
 Capture screenshot
 </Button>
 </div>
 </div>

 {screenshot ? (
 <ScreenshotPreview
 screenshot={screenshot}
 onRemove={() => setScreenshot(null)}
 disabled={isSubmitting}
 />
 ) : (
 <div className="text-xs text-muted-foreground">
 Screenshot is optional. Console logs will be included automatically.
 </div>
 )}

 <ImageAttachmentsPreview
 files={photoFiles}
 onRemoveAt={(idx) => setPhotoFiles((prev) => prev.filter((_, i) => i !== idx))}
 disabled={isSubmitting}
 />

 <details className="text-xs text-muted-foreground">
 <summary className="cursor-pointer select-none">Show captured console logs ({consoleLogs.length})</summary>
 <pre className="mt-2 max-h-40 overflow-auto rounded border bg-muted/30 p-2 text-[10px] leading-snug">
 {JSON.stringify(consoleLogs, null, 2)}
 </pre>
 </details>
 </div>

 <div className="flex gap-2">
 <Button
 type="button"
 variant="outline"
 onClick={() => {
 clear();
 setPhotoFiles([]);
 closeFeedback();
 }}
 disabled={isSubmitting}
 >
 Cancel
 </Button>
 <Button type="button" onClick={handleSubmit} disabled={isSubmitting || !description.trim()}>
 {isSubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
 Send feedback
 </Button>
 </div>
 </div>
 );
}
